import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { templateService, templatePreviewUrl, type Template } from '../../services/template.service';
import { ComposerCard, ComposerIdeas } from '../landing/Composer';
import { useComposer } from '../landing/useComposer';
import { useApplyTemplate } from '../landing/useApplyTemplate';
import { BootOverlay } from '../landing/BootOverlay';
import { useLandingTheme } from '../landing/useLandingTheme';
import { FONT_SANS, FONT_SERIF } from '../landing/landingData';
import { EmptyBox, ProjectCard, SectionHead, TextLink, useDash } from './dashUi';

const greeting = () => {
    const h = new Date().getHours();
    return h < 11 ? 'Good morning' : h < 15 ? 'Good afternoon' : 'Good evening';
};

/** Dashboard Home: greeting + composer, recent projects, a row of templates. */
export const DashHome: React.FC = () => {
    const dash = useDash();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated } = useAuth();
    const [theme] = useLandingTheme();
    const composer = useComposer({ isAuthenticated, notify: (_k, m) => dash.notify('error', m), chipCount: 4 });
    const [templates, setTemplates] = useState<Template[]>([]);
    const { apply: applyTemplate } = useApplyTemplate({ isAuthenticated, launch: composer.launch, abortLaunch: composer.abortLaunch });

    useEffect(() => {
        void templateService.list().then((r) => { if (r.success && r.data) setTemplates(r.data.slice(0, 5)); });
    }, []);

    // Router state handed to Home: "Make a video" from a site card, or the signup
    // referral-code result. Consumed once.
    useEffect(() => {
        const st = location.state as { videoFrom?: string; codeNotice?: { tone: 'success' | 'error' | 'warn'; text: string } } | null;
        if (!st) return;
        if (st.codeNotice) dash.notify(st.codeNotice.tone, st.codeNotice.text);
        if (st.videoFrom) {
            const p = dash.projects.find((x) => x.id === st.videoFrom);
            if (!p && dash.projectsLoading) return; // wait for the list, then apply
            composer.pickProduct('video');
            if (p) composer.setSource({ id: p.id, name: p.name, url: p.status === 'live' ? 'Published' : 'Draft', thumb: p.thumb });
            document.querySelector('.hv-composer textarea')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        navigate(location.pathname, { replace: true, state: null });
    }, [location.state, dash.projectsLoading]); // eslint-disable-line react-hooks/exhaustive-deps

    const recent = dash.projects.slice(0, 4);

    return (
        <>
            <section data-screen-label="Home · Create" style={{ position: 'relative', overflowX: 'clip', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', minHeight: 'max(560px,calc(100vh - 170px))', padding: '96px 0 0', boxSizing: 'border-box', animation: 'hvRise 0.45s cubic-bezier(0.22,0.9,0.24,1) both' }}>
                <h1 style={{ position: 'relative', margin: 0, fontFamily: FONT_SERIF, fontWeight: 400, fontSize: 'clamp(38px,4.6vw,60px)', lineHeight: 1, letterSpacing: '-0.01em', textWrap: 'balance', wordSpacing: '0.06em' } as React.CSSProperties}>
                    {greeting()}, <span style={{ fontStyle: 'italic' }}>{dash.firstName}</span>
                </h1>
                <p style={{ position: 'relative', margin: '14px 0 0', fontSize: 15.5, color: 'var(--hv-body)' }}>What do you want to make today?</p>
                <ComposerCard {...composer.cardProps(dash.credits ?? 0, dash.openTopUp)} style={{ marginTop: 36, textAlign: 'left' }} />
                <ComposerIdeas chips={composer.chips} onShuffle={composer.shuffle} onFillChip={composer.fillChip} product={composer.product} showStats={false} style={{ marginTop: 28 }} />
            </section>

            <section data-screen-label="Home · Recent" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <SectionHead title="Recent projects" action={dash.projects.length > 0 ? <TextLink onClick={() => navigate('/dashboard/projects')}>View all</TextLink> : undefined} />
                {dash.projectsLoading ? (
                    <div style={{ padding: '24px 0', fontFamily: FONT_SANS, fontSize: 10, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>LOADING…</div>
                ) : recent.length ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(230px,1fr))', gap: 16 }}>
                        {recent.map((p) => <ProjectCard key={`${p.kind}-${p.id}`} p={p} />)}
                    </div>
                ) : (
                    <EmptyBox title="Nothing here yet" text="Describe your first site or video above, or start from one of the templates below." />
                )}
            </section>

            {templates.length > 0 && (
                <section data-screen-label="Home · Templates" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <SectionHead title="Start from a template" action={<TextLink onClick={() => navigate('/dashboard/templates')}>Browse all</TextLink>} />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 14 }}>
                        {templates.map((t) => (
                            <button key={t.id} type="button" className="hv-card" data-track="dash_template_use" data-track-label={t.id} onClick={() => void applyTemplate(t)} style={{ display: 'flex', flexDirection: 'column', padding: 0, border: 0, background: 'transparent', cursor: 'pointer', textAlign: 'left', color: 'var(--hv-ink)' }}>
                                <div className="hv-thumb" style={{ position: 'relative', width: '100%', height: 130, borderRadius: 8, border: '1px solid var(--hv-line)', background: 'var(--hv-page-2)', overflow: 'hidden' }}>
                                    {t.hasPreview
                                        ? <div role="img" aria-label={`${t.name} preview`} style={{ position: 'absolute', inset: 0, backgroundImage: `url("${templatePreviewUrl(t.id)}")`, backgroundSize: 'cover', backgroundPosition: 'top' }} />
                                        : <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'flex-end', padding: 12, background: 'linear-gradient(150deg,var(--hv-mint),var(--hv-page-2))', fontFamily: FONT_SERIF, fontSize: 20, color: 'var(--hv-mint-ink)', opacity: 0.7 }}>{t.name}</div>}
                                </div>
                                <div style={{ padding: '10px 2px 0' }}>
                                    <div className="hv-cardtitle" style={{ fontSize: 13.5, fontWeight: 700 }}>{t.name}</div>
                                    <div style={{ marginTop: 3, fontFamily: FONT_SANS, fontSize: 9.5, letterSpacing: '0.06em', color: 'var(--hv-muted)' }}>{`WEBSITE · ${t.category}`.toUpperCase()}</div>
                                </div>
                            </button>
                        ))}
                    </div>
                </section>
            )}

            {composer.boot && <BootOverlay copy={composer.boot} kind={composer.bootKind} dark={theme === 'dark'} />}
        </>
    );
};

export default DashHome;
